import m from 'mithril'
import type { ListSummary, Restaurant, Review } from '../../shared/types.ts'
import { api } from '../api.ts'
import { state } from '../state.ts'
import { ErrorNote, RatingPicker, Stars, fullAddress, visibilityLabel } from '../ui.ts'
import { PinMap } from './PinMap.ts'
import { Sheet } from './Sheet.ts'

type Detail = { restaurant: Restaurant; myReview: Review | null; savedTo: string[] }

// The two starter lists, found by slug. Renaming one changes its slug, so the button then
// starts a fresh list under the default name.
const QUICK = {
  favorites: { slug: 'favorites', name: 'Favorites' },
  wantToGo: { slug: 'want-to-go', name: 'Want to go' },
}
const QUICK_SLUGS = new Set(Object.values(QUICK).map((list) => list.slug))

const ICONS = {
  heart: 'M12 20.3s-7.8-4.7-7.8-10.4A4.4 4.4 0 0 1 12 7.2a4.4 4.4 0 0 1 7.8 2.7c0 5.7-7.8 10.4-7.8 10.4z',
  flag: 'M5.5 21V4.5M5.5 4.5h12l-2.6 4.2 2.6 4.3h-12',
  message: 'M20 11.5a7.5 7.5 0 0 1-11 6.6L4.5 19.5l1.4-4.2A7.5 7.5 0 1 1 20 11.5z',
  listAdd: 'M4 6.5h11M4 12h11M4 17.5h7M18.5 14.5v6M15.5 17.5h6',
}

const icon = (d: string, filled: boolean) =>
  m(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: 22,
      height: 22,
      fill: filled ? 'currentColor' : 'none',
      stroke: 'currentColor',
      'stroke-width': 1.9,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
    },
    m('path', { d }),
  )

type Attrs = {
  place: Restaurant
  /** Called after the viewer's review changes, so the page can refresh its review list. */
  onreview?: () => void
}

/** A restaurant: map, details, and Favorite / Want to go / Review / Add to list actions. */
export const PlaceCard: m.ClosureComponent<Attrs> = ({ attrs }) => {
  let place = attrs.place
  let onreview = attrs.onreview
  let loadedRef = ''
  let myReview: Review | null = null
  let saved = new Set<string>()
  let lists: ListSummary[] = []
  let sheet: 'review' | 'lists' | null = null
  let error = ''

  let rating = 0
  let notes = ''
  let visitedOn = ''
  let busy = false
  let newList = ''

  const load = async (ref: string) => {
    loadedRef = ref
    try {
      const [detail, mine] = await Promise.all([
        api<Detail>(`/api/restaurants/${ref}`),
        state.me ? api<ListSummary[]>('/api/me/lists') : Promise.resolve([]),
      ])
      if (loadedRef !== ref) return
      place = detail.restaurant
      myReview = detail.myReview
      saved = new Set(detail.savedTo)
      lists = mine
      error = ''
    } catch (err) {
      error = (err as Error).message
    }
    m.redraw()
  }

  const signInFirst = () => {
    if (state.me) return false
    m.route.set('/login')
    return true
  }

  const toggle = async (listId: string) => {
    const had = saved.has(listId)
    const list = lists.find((candidate) => candidate.id === listId)
    const flip = (on: boolean) => {
      if (on) saved.add(listId)
      else saved.delete(listId)
      if (list) list.itemCount += on ? 1 : -1
    }
    // Flip first so the icon responds instantly; undo if the server says no.
    flip(!had)
    try {
      await api(`/api/lists/${listId}/items/${place.ref}`, { method: had ? 'DELETE' : 'PUT' })
    } catch (err) {
      flip(had)
      error = (err as Error).message
    }
    m.redraw()
  }

  const quickList = (which: keyof typeof QUICK) => lists.find((list) => list.slug === QUICK[which].slug)

  const toggleQuick = async (which: keyof typeof QUICK) => {
    if (signInFirst()) return
    let list = quickList(which)
    if (!list) {
      list = await api<ListSummary>('/api/me/lists', { method: 'POST', body: { name: QUICK[which].name } })
      lists = [list, ...lists]
    }
    await toggle(list.id)
  }

  const openReview = () => {
    if (signInFirst()) return
    rating = myReview?.rating ?? 0
    notes = myReview?.body ?? ''
    visitedOn = myReview?.visitedOn ?? ''
    sheet = 'review'
  }

  const saveReview = async (event: Event) => {
    event.preventDefault()
    if (!rating) {
      error = 'Pick a rating first.'
      return
    }
    busy = true
    try {
      await api(`/api/restaurants/${place.ref}/review`, {
        method: 'PUT',
        body: { rating, body: notes, visitedOn: visitedOn || null },
      })
      sheet = null
      await load(place.ref)
      onreview?.()
    } catch (err) {
      error = (err as Error).message
    } finally {
      busy = false
      m.redraw()
    }
  }

  const deleteReview = async () => {
    busy = true
    try {
      await api(`/api/restaurants/${place.ref}/review`, { method: 'DELETE' })
      sheet = null
      await load(place.ref)
      onreview?.()
    } finally {
      busy = false
      m.redraw()
    }
  }

  const createList = async (event: Event) => {
    event.preventDefault()
    const name = newList.trim()
    if (!name) return
    busy = true
    try {
      const list = await api<ListSummary>('/api/me/lists', { method: 'POST', body: { name } })
      lists = [list, ...lists]
      newList = ''
      await toggle(list.id)
    } catch (err) {
      error = (err as Error).message
    } finally {
      busy = false
      m.redraw()
    }
  }

  const action = (label: string, d: string, on: boolean, onclick: () => void) =>
    m(
      'button.action',
      { type: 'button', class: on ? 'on' : '', 'aria-pressed': on, onclick },
      [m('span.action-icon', icon(d, on)), m('span.action-label', label)],
    )

  return {
    oninit: () => load(place.ref),
    onbeforeupdate: ({ attrs: next }) => {
      onreview = next.onreview
      if (next.place.ref !== loadedRef) {
        place = next.place
        myReview = null
        saved = new Set()
        sheet = null
        load(next.place.ref)
      }
    },
    view: () => {
      const favorites = quickList('favorites')
      const wantToGo = quickList('wantToGo')
      const custom = lists.filter((list) => !QUICK_SLUGS.has(list.slug))
      const inCustom = custom.some((list) => saved.has(list.id))

      return m('section.card.selected-place', [
        m(PinMap, { pin: { lat: place.lat, lon: place.lon } }),
        m('div.selected-body', [
          m('h1', place.name),
          place.cuisine ? m('p.place-cuisine', place.cuisine) : null,
          m('p.muted', fullAddress(place) || 'No address on record'),
          place.createdBy != null && place.createdBy === state.me?.id ? m('p.muted.small-note', 'Added by you') : null,
          m('div.review-meta', [
            place.avgRating != null ? m(Stars, { value: place.avgRating }) : null,
            m(
              'span.muted',
              place.reviewCount
                ? `${place.avgRating?.toFixed(1)} from ${place.reviewCount} review${place.reviewCount > 1 ? 's' : ''}`
                : 'No reviews yet',
            ),
          ]),
          place.website || place.phone
            ? m('div.place-links', [
                place.website ? m('a', { href: place.website, target: '_blank', rel: 'noopener' }, 'Website') : null,
                place.phone ? m('a', { href: `tel:${place.phone}` }, place.phone) : null,
              ])
            : null,

          m('div.actions', [
            action('Favorite', ICONS.heart, favorites != null && saved.has(favorites.id), () => toggleQuick('favorites')),
            action('Want to go', ICONS.flag, wantToGo != null && saved.has(wantToGo.id), () => toggleQuick('wantToGo')),
            action(myReview ? `${myReview.rating} ★` : 'Review', ICONS.message, myReview != null, openReview),
            action('Add to list', ICONS.listAdd, inCustom, () => {
              if (!signInFirst()) sheet = 'lists'
            }),
          ]),
          error ? m(ErrorNote, { text: error }) : null,
        ]),

        sheet === 'review'
          ? m(Sheet, { title: myReview ? 'Your review' : 'Add a review', onclose: () => (sheet = null) }, [
              m('form.stack', { onsubmit: saveReview }, [
                m('p.muted', place.name),
                m(RatingPicker, {
                  value: rating,
                  onchange: (value: number) => {
                    rating = value
                  },
                }),
                m('label', [
                  'Notes',
                  m('textarea', {
                    rows: 4,
                    value: notes,
                    placeholder: 'Notes',
                    oninput: (event: Event) => {
                      notes = (event.target as HTMLTextAreaElement).value
                    },
                  }),
                ]),
                m('label', [
                  'Visited on',
                  m('input', {
                    type: 'date',
                    value: visitedOn,
                    max: new Date().toISOString().slice(0, 10),
                    oninput: (event: Event) => {
                      visitedOn = (event.target as HTMLInputElement).value
                    },
                  }),
                ]),
                m('button.button.primary.block', { type: 'submit', disabled: busy || !rating }, 'Save review'),
                myReview
                  ? m('button.button.danger.block', { type: 'button', disabled: busy, onclick: deleteReview }, 'Delete review')
                  : null,
              ]),
            ])
          : null,

        sheet === 'lists'
          ? m(Sheet, { title: 'Add to list', onclose: () => (sheet = null) }, [
              custom.length
                ? m(
                    'div.list-group',
                    custom.map((list) =>
                      m('label.list-row.check-row', { key: list.id }, [
                        m('div.list-row-main', [
                          m('div.list-row-title', list.name),
                          m('div.list-row-meta', `${list.itemCount} place${list.itemCount === 1 ? '' : 's'}${list.visibility === 'public' ? '' : ` · ${visibilityLabel(list.visibility)}`}`),
                        ]),
                        m('input', {
                          type: 'checkbox',
                          checked: saved.has(list.id),
                          onchange: () => toggle(list.id),
                        }),
                      ]),
                    ),
                  )
                : null,
              m('form.new-list', { onsubmit: createList }, [
                m('input', {
                  value: newList,
                  placeholder: 'New list name',
                  'aria-label': 'New list name',
                  oninput: (event: Event) => {
                    newList = (event.target as HTMLInputElement).value
                  },
                }),
                m('button.button.primary', { type: 'submit', disabled: busy || !newList.trim() }, 'Create'),
              ]),
            ])
          : null,
      ])
    },
  }
}
